import AdContainer from 'components/AdContainer';
import BigPlayButton from 'components/BigPlayButton';
import InteractiveLayer from 'components/Interactive/InteractiveLayer';
import AdSkin from 'containers/AdSkin';
import PlayerSkin from 'containers/PlayerSkin';
import { TranslateMapContext } from 'contexts/TranslateMapContext';
import { get, isEqual, throttle } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { withTheme } from 'styled-components';
import { translateKeysCMS } from 'translates/translateKeysCMS';
import { GradientLoader } from '../../components/Loader/GradientLoader';
import { PIP_STUB_CONTAINER_ID } from '../../components/newSkin/PipStub/PipStub';
import { RootDynamicDataContext } from '../../contexts/RootDynamicDataContext';
import { getTypeOfContent } from '../../utils/getTypeOfContent';
import { updateInteractiveTime } from '../../utils/interactiveTimeWatcher';
import { isAndroid, isMobile } from '../../utils/isMobile';
// import logger from 'utils/logger';

const isAllwaysShowSkinDev = window.location.hash.includes(
  'alwaysshowskin=true'
);

class SkinRoot extends Component {
  static propTypes = {
    data: PropTypes.object.isRequired,
    playerMethods: PropTypes.object.isRequired,
    adMethods: PropTypes.object.isRequired,
    socials: PropTypes.array.isRequired,
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    onMounted: PropTypes.func.isRequired,
    theme: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.adContainerRef = React.createRef();
    this.providedTranslateData = {
      ...translateKeysCMS,
      translateFunc: props.playerMethods.translate,
      getTranslate: Object.entries(translateKeysCMS).reduce(
        (accum, [key, value]) => {
          accum[key] = (params) => props.playerMethods.translate(value, params);
          return accum;
        },
        {}
      ),
    };
    this.rootDynamicDataValue = null;

    const container = document.getElementById('playerContainer');

    this.state = {
      showBigPlay: false,
      playerWidth: container?.clientWidth || 0,
      playerHeight: container?.clientHeight || 0,
    };

    this.onContainerObserverThrottled = throttle(
      this.onContainerObserver,
      300,
      {
        leading: false,
        trailing: true,
      }
    );

    this.containerObserver = new ResizeObserver(
      this.onContainerObserverThrottled
    );
    if (container) {
      this.containerObserver.observe(container);
    }
  }

  static getDerivedStateFromProps(props) {
    const data = props.data;
    let needShow = data.loadCalled && !data.isAutoplay && !data.messageModeData;
    if (data.started) needShow = false;

    return {
      showBigPlay: needShow,
    };
  }

  componentDidMount() {
    this.props.onMounted();
  }

  componentWillUnmount() {
    this.containerObserver.disconnect();
  }

  componentDidUpdate() {
    const { data, playerMethods } = this.props;
    if (data.adPlaying && isMobile && !isAndroid && data.isFullscreen) {
      playerMethods.exitFullscreen();
    }
    updateInteractiveTime(data);
  }

  onContainerObserver = (entries) => {
    const [entry] = entries;
    this.setState({
      playerWidth: entry.contentRect.width,
      playerHeight: entry.contentRect.height,
    });
  };

  getPlayerDims = () => {
    const playerContainer = document.getElementById('playerContainer');
    return {
      playerHeight: playerContainer.clientHeight,
      playerWidth: playerContainer.clientWidth,
    };
  };

  onBigPlayClick = () => {
    this.props.playerMethods.setAutoplay(true);
    this.setState({
      showBigPlay: false,
    });
  };

  onSeek = (time) => {
    const { playerMethods } = this.props;
    playerMethods.seek(
      time
      // data.channelState,
      // getTypeOfContent(data.stream),
      // get(data.stream, 'content_type'),
      // data.channel ? data.channel.type : undefined
    );
  };

  onNextClick = () =>
    this.props.playerMethods.nextEpisode(this.props.data.nextObject);

  onPrevClick = () =>
    this.props.playerMethods.prevEpisode(this.props.data.prevObject);

  backOnAir = () => {
    const { playerMethods } = this.props;
    // playerMethods.backOnAir(get(data.stream, 'content_type', ''));
    playerMethods.backOnAir();
  };

  skipIntroStart = (skipIntroText) => {
    this.setState({
      disableLoaders: true,
    });
    return this.props.playerMethods.skipIntro(skipIntroText);
  };

  disableLoader = () => {
    this.setState({
      disableLoaders: false,
    });
  };

  nextProgram = () => {
    const { data, playerMethods } = this.props;
    playerMethods.nextProgram(
      data.channel,
      data.program,
      getTypeOfContent(data.stream),
      get(data.stream, 'content_type', '')
    );
  };

  prevProgram = () => {
    const { data, playerMethods } = this.props;
    playerMethods.prevProgram(
      data.channel,
      data.program,
      getTypeOfContent(data.stream),
      get(data.stream, 'content_type', '')
    );
  };

  getRootDynamicDataValue = (data) => {
    if (!isEqual(this.rootDynamicDataValue, data)) {
      this.rootDynamicDataValue = data;
    }
    return this.rootDynamicDataValue;
  };

  render() {
    const { data, playerMethods, adMethods, socials, userId } = this.props;
    const { showBigPlay } = this.state;

    // think about moving these to PlayerState when the old mobile skin has been gone away
    const renderAdSkin =
      data.adLinear && data.adPlaying && !data.isHybridAdSkin;
    const isLinearHybridAdShowed =
      Boolean(data.adLinear && data.adPlaying && data.isHybridAdSkin) ||
      window.isHybrid;
    const isSkinHidden =
      !isAllwaysShowSkinDev &&
      !isLinearHybridAdShowed &&
      (renderAdSkin ||
        showBigPlay ||
        (!data.mainContentStarted && !data.smallMode) ||
        data.cmsWantsToHideSkin) &&
      !data.messageModeData;

    //MHP-1865
    let programPosition = isLinearHybridAdShowed ? 0 : data.programPosition;
    let adTimeLabels = isLinearHybridAdShowed ? [] : data.adTimeLabels;
    let currentProgramTime = isLinearHybridAdShowed
      ? (data.programDuration / 100) * data.adProgress
      : data.currentProgramTime;
    let currentTime = isLinearHybridAdShowed ? 0 : data.currentTime;

    const isShowLoader =
      data.showMainLoader ||
      (!this.state.disableLoaders &&
        !data.disableLoaders &&
        data.isBuffering &&
        !data.messageModeData);

    return (
      <RootDynamicDataContext.Provider
        value={this.getRootDynamicDataValue({ isLinearHybridAdShowed, userId })}
      >
        <div id={PIP_STUB_CONTAINER_ID} />
        <TranslateMapContext.Provider value={this.providedTranslateData}>
          <AdContainer
            target={this.adContainerRef}
            type={data.memberIsStarting ? data.adType : ''}
            isHidden={!data.memberIsStarting}
            isAdLinear={data.adPlaying ? data.adLinear : null}
          />
          <InteractiveLayer
            {...window.mggPlayer.config.interactive}
            registerEvent={playerMethods.registerRemoteEvent}
            unRegisterEvent={playerMethods.unRegisterRemoteEvent}
          />
          <PlayerSkin
            isLinearHybridAdShowed={isLinearHybridAdShowed}
            isAdIsTitleVisible={data.adIsTitleVisible}
            adType={data.memberIsStarting ? data.adType : ''}
            adTitleTxt={data.adTitleTxt}
            isRootHidden={isSkinHidden}
            isLoaded={
              data.started &&
              !data.showMainLoader &&
              !showBigPlay &&
              !data.paused
            }
            disableLoaders={data.disableLoaders}
            playerWidth={this.state.playerWidth}
            playerHeight={this.state.playerHeight}
            // env props
            isMobile={isMobile}
            isAndroid={isAndroid}
            // player's properties
            currentTime={currentTime}
            duration={data.duration}
            loadedFraction={data.loadedFraction}
            volume={data.volume}
            paused={data.paused}
            ended={data.ended}
            muted={data.muted}
            timeToAdStart={data.adTimeToAdStart}
            subtitlesText={data.subtitlesText}
            qualityList={data.qualityList}
            audioTrackList={data.audioTrackList}
            subtitlesList={data.subtitlesList}
            interactivePreferences={data.interactivePreferences}
            thumbnails={data.thumbnails}
            isBuffering={data.isBuffering}
            audioTrack={data.audioTrack}
            subtitles={data.subtitles}
            quality={data.quality}
            isFullscreen={data.isFullscreen}
            socials={socials}
            userId={userId}
            messageModeData={data.messageModeData}
            isFavorite={
              data.isFavorite !== null
                ? data.isFavorite
                : get(data.channel, 'isFavorite', false)
            }
            isFavoritePhrase={data.isFavoritePhrase}
            chromecastState={data.chromecastState}
            playerVideoElement={get(data.videoInstances, 0)}
            videoPlayerInstance={data.videoPlayerInstance}
            smallMode={data.smallMode}
            getCurrentTimestamp={playerMethods.getCurrentTimestamp}
            autoQualityIndex={data.autoQualityIndex}
            livePosition={data.livePosition}
            liveDuration={data.liveDuration}
            liveIsTimeshift={data.liveIsTimeshift}
            liveTime={isLinearHybridAdShowed ? 0 : data.liveTime}
            remindInfo={data.remindInfo}
            isPipModeOn={data.isPipModeOn}
            pipIsSupported={data.pipIsSupported}
            isInteractiveFullscreen={data.isInteractiveFullscreen}
            playlistInfo={data.playlistInfo}
            playlistItem={data.playlistItem}
            // advert properties
            adVastData={data.adVastData}
            adCurrentTime={data.adCurrentTime}
            adPlaying={data.adPlaying}
            adTimeLabels={adTimeLabels}
            adContainer={this.adContainerRef.current}
            adActionClose={adMethods.adClose}
            adActionClick={adMethods.adClickOnBg}
            adActionResume={adMethods.adResume}
            isAdLinear={data.adLinear}
            // stream props
            isWatermarkClickable={data.isWatermarkClickable}
            srcWaterMark={data.srcWaterMark}
            shareLink={data.shareLink}
            rootVideoId={data.rootVideoId}
            type={getTypeOfContent(data.stream)}
            contentType={get(data.stream, 'content_type', '')}
            nextObject={data.nextObject}
            prevObject={data.prevObject}
            nextProgramObject={
              data.channel ? data.channel.getNextProgram(data.program) : {}
            }
            prevProgramObject={
              data.channel ? data.channel.getPrevProgram(data.program) : {}
            }
            programDuration={data.programDuration}
            programPosition={programPosition}
            currentProgramTime={currentProgramTime}
            channelState={data.channelState}
            currentChannel={data.channel}
            title={data.title}
            program={data.program || data.currentProgram}
            showCloseButton={data.showCloseButton}
            showSkipIntroButton={data.showSkipIntroButton}
            skipIntroSkipped={data.skipIntroSkipped}
            showFavoriteButton={data.showFavoriteButton}
            showShareButton={data.showShareButton}
            showVolumeButton={data.showVolumeButton}
            isAbleToHideSkin={data.isAbleToHideSkin}
            // player's functions
            play={playerMethods.play}
            pause={playerMethods.pause}
            seek={this.onSeek}
            mute={playerMethods.mute}
            unMute={playerMethods.unMute}
            nextChannel={playerMethods.nextChannel}
            prevChannel={playerMethods.prevChannel}
            nextProgram={this.nextProgram}
            prevProgram={this.prevProgram}
            setVolume={playerMethods.setVolume}
            setAudioTrack={playerMethods.setAudioTrack}
            setQuality={playerMethods.setQuality}
            setSubtitles={playerMethods.setSubtitles}
            setMessageContainer={playerMethods.setMessageContainer}
            toggleFavorite={playerMethods.toggleFavorite}
            emitChromecastAction={playerMethods.emitChromecastAction}
            setSkipAdProperty={playerMethods.setSkipAdProperty}
            backOnAir={this.backOnAir}
            skipIntroStart={this.skipIntroStart}
            disableLoader={this.disableLoader}
            onBitrateBySubscriptionButtonClick={
              playerMethods.onBitrateBySubscriptionButtonClick
            }
            onLiveDvrTimeBarClick={playerMethods.onLiveDvrTimeBarClick}
            onClickRemindIcon={playerMethods.onClickRemindIcon}
            onClickPipButton={playerMethods.onClickPipButton}
            onSharePopupOpen={playerMethods.onSharePopupOpen}
            onClickShareButton={playerMethods.onClickShareButton}
            onClickVolumeButton={playerMethods.onClickVolumeButton}
            // callbacks
            onRetry={playerMethods.retry}
            onShareOpen={Function.prototype}
            onShareClose={Function.prototype}
            onEpisodeOpen={Function.prototype}
            onEpisodeClose={Function.prototype}
            onCloseClick={playerMethods.onCloseClick}
            onNextClick={this.onNextClick}
            onPrevClick={this.onPrevClick}
            enterFullscreen={playerMethods.enterFullscreen}
            exitFullscreen={playerMethods.exitFullscreen}
            // service methods
            translate={playerMethods.translate}
            monthToString={playerMethods.monthToString}
            emitSkinVisibilityBeginChange={
              playerMethods.emitSkinVisibilityBeginChange
            }
            emitSafeZoneSizeChanged={playerMethods.emitSafeZoneSizeChanged}
          />
          {renderAdSkin && (
            <AdSkin
              count={data.adCount}
              number={data.adNumber}
              paused={data.adPaused}
              volume={data.volume}
              muted={data.muted}
              skipTime2={data.adSkipTime2}
              duration={data.adDuration}
              isClickable={data.adIsClickable}
              currentTime={data.adCurrentTime}
              skipButton={data.adSkipButton}
              progress={data.adProgress}
              isSkipDisabled={data.adIsSkipDisabled}
              skipLeft={data.adSkipLeft}
              url={data.adUrl}
              isTitleVisible={data.adIsTitleVisible}
              titleTxt={data.adTitleTxt}
              linkTxt={data.adLinkTxt}
              progressVisible={data.adProgressVisible}
              controls={data.adControls}
              isImageMediaFile={data.adIsImageMediaFile}
              isVPAID={data.adIsVPAID}
              isImaAd={data.adIsImaAd}
              translate={playerMethods.translate}
              skip={adMethods.adSkip}
              close={adMethods.adClose}
              clickOnBg={adMethods.adClickOnBg}
              clickMessage={adMethods.adClickMessage}
              resume={adMethods.adResume}
              mute={adMethods.adMute}
              unMute={adMethods.adUnMute}
              setVolume={adMethods.adSetVolume}
              isMobile={isMobile}
              isAndroid={isAndroid}
              enterFullscreen={playerMethods.enterFullscreen}
              exitFullscreen={playerMethods.exitFullscreen}
              isFullscreen={data.isFullscreen}
              isSmallMode={data.smallMode}
            />
          )}
          {isShowLoader && <GradientLoader />}
          {showBigPlay && !data.messageModeData && (
            <BigPlayButton
              onMouseDown={this.onBigPlayClick}
              backgroundPoster={data.backgroundPoster}
              playerWidth={this.state.playerWidth}
            />
          )}
        </TranslateMapContext.Provider>
      </RootDynamicDataContext.Provider>
    );
  }
}

export default withTheme(SkinRoot);
